import React, { useState, useRef, useEffect, TouchEvent } from 'react';
import './ZoomableImage.css';

interface ZoomableImageProps {
    children: React.ReactNode;
    className?: string;
    onDoubleTap?: () => void;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({
    children,
    className = '',
    onDoubleTap
}) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    // Touch state
    const lastTouchRef = useRef<{ x: number, y: number } | null>(null);
    const lastDistanceRef = useRef<number | null>(null);
    const lastTapRef = useRef<number>(0);

    // Reset zoom when children change (conceptually new image)
    // However, children might change due to loading state (blur -> sharp).
    // We probably shouldn't reset on every render.
    // The parent Listing.tsx mounts a new ZoomableImage or we rely on key prop in parent to reset.

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
            setIsDragging(true);

            const now = Date.now();
            if (now - lastTapRef.current < 300) {
                handleDoubleTap(touch.clientX, touch.clientY);
            }
            lastTapRef.current = now;

        } else if (e.touches.length === 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
            lastDistanceRef.current = dist;
        }
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        if (e.touches.length === 1 && isDragging) {
            const touch = e.touches[0];
            if (lastTouchRef.current) {
                const deltaX = touch.clientX - lastTouchRef.current.x;
                const deltaY = touch.clientY - lastTouchRef.current.y;

                if (scale > 1) {
                    setPosition(prev => ({
                        x: prev.x + deltaX,
                        y: prev.y + deltaY
                    }));
                    e.stopPropagation();
                }

                lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
            }
        } else if (e.touches.length === 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);

            if (lastDistanceRef.current !== null) {
                const delta = dist - lastDistanceRef.current;
                // Sensitivity factor 0.005 for smoother zoom
                const newScale = Math.min(Math.max(1, scale + delta * 0.005), 4);
                setScale(newScale);

                if (newScale === 1) {
                    setPosition({ x: 0, y: 0 });
                }
            }
            lastDistanceRef.current = dist;
            e.stopPropagation();
        }
    };

    const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
        setIsDragging(false);
        lastDistanceRef.current = null;

        // Optional: Snap back if out of bounds (omitted for simplicity)
    };

    const handleDoubleTap = (clientX: number, clientY: number) => {
        if (scale > 1) {
            setScale(1);
            setPosition({ x: 0, y: 0 });
        } else {
            setScale(2.5);
            setPosition({ x: 0, y: 0 });
        }
        if (onDoubleTap) onDoubleTap();
    };

    return (
        <div
            className={`zoomable-image-container ${className}`}
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={(e) => {
                if (scale > 1) {
                    e.stopPropagation();
                }
            }}
        >
            <div
                className="zoomable-content"
                style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default ZoomableImage;
